import { Box, Link, Table, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

export interface ProcessorInstancesTableRow {
  id: string;
  processorId: string;
  status: string;
  error?: string | null;
  createdAt: string | number | Date;
}

interface ProcessorInstancesTableProps {
  rows: ProcessorInstancesTableRow[];
  loading: boolean;
  /** When true, add a column linking to the processor detail page */
  showProcessorColumn?: boolean;
  emptyMessage?: string;
  loadingText?: string;
}

export function ProcessorInstancesTable({
  rows,
  loading,
  showProcessorColumn = false,
  emptyMessage = 'No processor instances yet.',
  loadingText = 'Loading…',
}: ProcessorInstancesTableProps) {
  if (loading) {
    return (
      <Text color="fg.muted" fontSize="sm">
        {loadingText}
      </Text>
    );
  }

  if (rows.length === 0) {
    return (
      <Text color="fg.muted" fontSize="sm">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      <Table.Root size="sm" variant="line">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Created</Table.ColumnHeader>
            {showProcessorColumn ? (
              <Table.ColumnHeader>Processor</Table.ColumnHeader>
            ) : null}
            <Table.ColumnHeader>Status</Table.ColumnHeader>
            <Table.ColumnHeader>Error</Table.ColumnHeader>
            <Table.ColumnHeader>Id</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => (
            <Table.Row key={row.id}>
              <Table.Cell whiteSpace="nowrap">
                {new Date(row.createdAt).toLocaleString()}
              </Table.Cell>
              {showProcessorColumn ? (
                <Table.Cell>
                  <Link asChild color="blue.600">
                    <RouterLink to={`/processors/${row.processorId}`}>
                      {row.processorId}
                    </RouterLink>
                  </Link>
                </Table.Cell>
              ) : null}
              <Table.Cell>{row.status}</Table.Cell>
              <Table.Cell
                fontSize="xs"
                maxW="200px"
                css={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={row.error ?? undefined}
              >
                {row.error ?? '—'}
              </Table.Cell>
              <Table.Cell fontFamily="mono" fontSize="xs">
                {row.id}
              </Table.Cell>
              <Table.Cell textAlign="end">
                <Link asChild color="blue.600">
                  <RouterLink to={`/instance/${row.id}`}>View</RouterLink>
                </Link>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
